import { CourseTopics } from "./topics/CourseTopics";
import { useQuery } from "@tanstack/react-query";
import { coursesApi } from "@/lib/api/courses";
import { useGenerateClasses, useGenerateSessionsBox } from "./use-generate-sessions";

import { AllSessionsRegister } from "./AllSessionsRegister";
import type { Read } from "@/components/ui/box.schema";
import { TileHeadingContext } from "@/components/ui/tile-heading-context";
import { useAuth } from "@/contexts/AuthContext";
import type { CourseWorkspaceShell } from "@/navigation/routes";

/** All sessions for the teaching team, with Generate sessions and the plans not on a session yet (C07, C08). */
export function CoursePlan({ courseId, timezone, termOpen, canEdit, shell, headingLevel = "h2", emptySignal, preparation = true, calendarOrder = false }: {
  preparation?: boolean;
  /** The Desk's Calendar order: the register without Topics, which Outline draws (#1393 M2). */
  calendarOrder?: boolean;
  courseId: string; timezone: string; termOpen: boolean; canEdit: boolean;
  /** No longer read: the plan on the current session is All sessions' own row. */
  currentUnitId?: string | null;
  shell: CourseWorkspaceShell; portalEnabled: boolean; headingLevel?: "h2" | "h3"; emptySignal?: boolean;
  /** No longer read: the calendar subscription is the course frame's own Task (O1), never this register's head act. */
  calendar?: boolean;
}) {
  const { me } = useAuth();
  const generate = useGenerateClasses(courseId);
  const recorded = useQuery({ queryKey: ["course-information", me?.tenant_id, me?.user_id, courseId], queryFn: () => coursesApi.get(courseId), enabled: canEdit && termOpen, retry: false });
  const recurrence = recorded.data?.id === courseId && (recorded.data.meeting_weekdays?.length && recorded.data.meeting_starts_at && recorded.data.meeting_ends_at || recorded.data.meeting_days && recorded.data.meeting_time);
  const meetingDays: Read<null> = recorded.isError ? { status: "failed", sentence: "The course's meeting days couldn't be read.", retry: () => void recorded.refetch() }
    : !recorded.data ? { status: "loading" }
      : recurrence ? { status: "ready", value: null, readAt: new Date(recorded.dataUpdatedAt).toISOString() }
        : { status: "empty", sentence: "Set the course's meeting days and times in its settings to generate its sessions." };
  const generateBox = useGenerateSessionsBox(generate, meetingDays);
  const canGenerate = canEdit && termOpen;
  return (
    <TileHeadingContext.Provider value={headingLevel}>
      {!calendarOrder && <CourseTopics courseId={courseId} teaching={preparation} />}
      <AllSessionsRegister courseId={courseId} timezone={timezone} teaching canEdit={canEdit} termOpen={termOpen} shell={shell}
        emptySignal={emptySignal} generate={canGenerate ? generateBox : null} unplaced calendarOrder={calendarOrder} />
    </TileHeadingContext.Provider>
  );
}
